import { RootStore } from './root.store';
import { makeAutoObservable } from 'mobx';
import { AddressType, IAddress, IContact } from '../domains/common.domain';
import { IBillingInformation } from '../domains/billing-information.domain';
import { TaxIdFormat } from '../domains/tenant.domain';
import { getTaxIdHelper } from '../utils/tax-id-helper';
import { isEmailValid, isPhoneNumberValid, validateNationalId } from '../utils/validators';

export class BillingInformationStore {
  root: RootStore;
  private _addressType: AddressType = 'billing';
  private _isSameAsOnboardingContacts: boolean = false;
  private _information: IBillingInformation = {
    nationalTaxId: { value: '', errorType: 'required' },
    stateTaxId: { value: '', errorType: undefined },
    invoiceAnalysisContactEmail: { value: '', errorType: 'required' },
    invoiceAnalysisContactPhone: { value: '', errorType: 'required' },
    invoiceDueDay: { value: '', errorType: 'required' },
    signerFirstName: { value: '', errorType: 'required' },
    signerLastName: { value: '', errorType: 'required' },
    signatoryPosition: { value: '', errorType: 'required' },
    nationalId: { value: '', errorType: 'required' },
  };

  constructor(root: RootStore) {
    makeAutoObservable(this);
    this.root = root;
  }

  init() {
    const { populateBillingInfo, invoiceDueDays } = this.root.tenantStore.config;
    this.root.billingAddressStore.init('billing');
    if (populateBillingInfo) {
      this.root.quoteStore.quoteInfo?.customer?.contacts?.forEach((contact, index) => {
        if (index > 0) {
          this.root.billingContactsStore.addContact();
        }
        this.updateContact(index, 'firstName', contact.fname);
        this.updateContact(index, 'lastName', contact.lname);
        this.updateContact(index, 'email', contact.email);
        this.updateContact(index, 'phoneNumber', contact.phone);
      });
      const taxIdHelper = getTaxIdHelper(this.root.tenantStore.config.taxIdFormat);
      const nationalTaxId = this.root.quoteStore.quoteInfo?.customer?.customerTaxId ?? '';
      this.updateInformation('nationalTaxId', taxIdHelper?.getFormattedTaxId(nationalTaxId) ?? nationalTaxId);
      if (this.root.quoteStore.quoteInfo?.customer?.contacts) {
        this.updateInformation('signerFirstName', this.root.quoteStore.quoteInfo?.customer?.contacts[0]?.fname ?? '');
        this.updateInformation('signerLastName', this.root.quoteStore.quoteInfo?.customer?.contacts[0]?.lname ?? '');
      }

      const defaultInvoiceDueDays = '25';
      if (invoiceDueDays.split(',').includes(defaultInvoiceDueDays)) {
        this.updateInformation('invoiceDueDay', defaultInvoiceDueDays);
      }
    }

    this._information.stateTaxId.errorType = this.root.tenantStore.config.taxIdFormat === TaxIdFormat.Brazil ? undefined : 'required';
  }

  get information(): IBillingInformation {
    return this._information;
  }

  updateInformation(field: keyof IBillingInformation, value: string) {
    const { taxIdFormat, nationalIdMaxLength, minPhoneNumberLength } = this.root.tenantStore.config;
    const taxIdHelper = getTaxIdHelper(taxIdFormat);
    if (field === 'stateTaxId') {
      value = taxIdHelper?.getFormattedStateTaxId(value) ?? value;
      this._information[field] = {
        value: value,
        errorType: taxIdHelper?.validateStateTaxId(value) ?? undefined,
      };
      return;
    }
    if (field === 'nationalId') {
      value = taxIdHelper?.getFormattedNationalId(value) ?? value;
      this._information[field] = {
        value: value,
        errorType: validateNationalId(value, taxIdFormat, nationalIdMaxLength),
      };
      return;
    }
    this._information[field] = {
      value: value,
      errorType: !value ? 'required' : undefined,
    };
    if (field === 'invoiceAnalysisContactEmail' && !isEmailValid(value)) {
      this._information[field].errorType = 'invalid';
    }
    if (field === 'invoiceAnalysisContactPhone' && !isPhoneNumberValid(value, minPhoneNumberLength)) {
      this._information[field].errorType = 'invalid';
    }
  }

  get addressType(): AddressType {
    return this._addressType;
  }

  get isFormValid(): boolean {
    const {
      displayStateTaxId,
      displayInvoiceAnalysisFields,
      invoiceDueDays,
      displaySignerInformation,
      displaySignatoryPosition,
      displayNationalId,
    } = this.root.tenantStore.config;
    return (
      this.root.billingAddressStore.isAddressValid &&
      this.root.billingContactsStore.isFormValid &&
      (displayStateTaxId ? !this._information.stateTaxId.errorType : true) &&
      (displayInvoiceAnalysisFields
        ? !this._information.invoiceAnalysisContactEmail.errorType && !this._information.invoiceAnalysisContactPhone.errorType
        : true) &&
      (invoiceDueDays ? !this._information.invoiceDueDay.errorType : true) &&
      (displaySignerInformation ? !this._information.signerFirstName.errorType && !this._information.signerLastName.errorType : true) &&
      (displaySignatoryPosition ? !this._information.signatoryPosition.errorType : true) &&
      (displayNationalId ? !this._information.nationalId.errorType : true)
    );
  }

  updateAddress(field: keyof IAddress, value: string) {
    this.resetAddressType();
    this.root.billingAddressStore.updateAddress(field, value);
  }

  resetAddressType() {
    this._addressType = 'billing';
  }

  set addressType(value: AddressType) {
    this._addressType = value;
    switch (value) {
      case 'billing':
        this.root.billingAddressStore.resetAddress();
        break;
      case 'service':
        this.root.billingAddressStore.changeAddress(this.root.serviceAddressStore.address);
        break;
      case 'shipping':
        this.root.billingAddressStore.changeAddress(this.root.shippingAddressStore.address);
        break;
    }
  }

  addContact() {
    this._isSameAsOnboardingContacts = false;
    this.root.billingContactsStore.addContact();
  }

  removeContact(index: number) {
    this._isSameAsOnboardingContacts = false;
    this.root.billingContactsStore.removeContact(index);
  }

  updateContact(index: number, field: keyof IContact, value: string) {
    this._isSameAsOnboardingContacts = false;
    this.root.billingContactsStore.updateContact(index, field, value);
  }

  updateSameAsOnboardingContacts() {
    this._isSameAsOnboardingContacts = !this._isSameAsOnboardingContacts;
    if (this._isSameAsOnboardingContacts) {
      this.root.billingContactsStore.contacts = this.root.onboardingContactsStore.contacts.map(x => ({ ...x }));
    } else {
      this.root.billingContactsStore.resetContacts();
    }
  }

  get isSameAsOnboardingContacts(): boolean {
    return this._isSameAsOnboardingContacts;
  }

  uncheckSameAsOnboardingContacts() {
    this._isSameAsOnboardingContacts = false;
  }
}
